import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ApiError, validateToken } from '@/lib'
import { useAuthStore } from '@/stores/auth'

interface UseAutoLoginParams {
  token?: string
  redirectPath?: string
}

interface UseAutoLoginReturn {
  isAutoLoginInProgress: boolean
  errorMessage: string | null
}

/**
 * OIDC コールバック後の自動ログイン処理を管理するカスタムフック
 * token が存在する場合、自動的にバリデーションとログイン処理を実行
 */
export function useAutoLogin({ token, redirectPath }: UseAutoLoginParams): UseAutoLoginReturn {
  const navigate = useNavigate()
  const { setUser } = useAuthStore()

  const [isAutoLoginInProgress, setIsAutoLoginInProgress] = useState(!!token)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return

    const autoLogin = async () => {
      try {
        const userData = await validateToken(token)
        setUser(userData)
        // redirectPath は _authenticated.tsx が生成した URL を decode したもので末尾に ? が付く場合がある
        // TanStack Router の navigate は '?' のみの末尾を正しく解決できないため除去する
        const targetPath = (redirectPath || '/').replace(/\?$/, '') || '/'
        await navigate({ to: targetPath })
      } catch (err) {
        console.error('Auto-login failed:', err)
        setIsAutoLoginInProgress(false)
        if (err instanceof ApiError) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage('ログインに失敗しました')
        }
      }
    }

    autoLogin()
  }, [token, redirectPath, setUser, navigate])

  return {
    isAutoLoginInProgress,
    errorMessage,
  }
}
